import {SelectItem, SelectPopover, SelectArrow, SelectList, ComboboxList, SelectGroupLabel} from '@ariakit/react'
import styled, {css} from 'styled-components'
import {Button} from '@components/ui/button/Button'
import {DefaultTheme} from 'styled-components/dist/types'
import {Virtuoso} from 'react-virtuoso'

export const DESKTOP_TABLE_SCROLLBAR_WIDTH = '4px'

export const makeSelectPopoverBaseStyle = ({palette, shadows, spacing, zIndex}: DefaultTheme) => css`
    background-color: ${palette.neutral[50]};
    border-radius: 8px;
    border: 1px solid ${palette.neutral[200]};
    box-shadow: ${shadows.lg};
    display: grid;
    max-height: 328px;
    max-width: var(--popover-available-width);
    min-width: var(--popover-anchor-width);
    overflow-y: hidden;
    padding: ${spacing * 1.5}px ${spacing * 1.5}px;
    grid-template-rows: minmax(0, 1fr);
    z-index: ${zIndex.dropdown};
`

export const StyledSelectPopover = styled(SelectPopover)(
    ({theme}) => css`
        ${makeSelectPopoverBaseStyle(theme)}
    `
)

export const StyledSelectComboboxPopover = styled(StyledSelectPopover)(
    ({theme: {spacing}}) => css`
        gap: ${spacing * 1.5}px;
        grid-template-rows: auto minmax(0, 1fr);
    `
)

export const StyledSelectGroupLabel = styled(SelectGroupLabel)(
    ({theme: {palette, spacing, typography, opacities}}) => css`
        ${typography.textXs};
        color: ${palette.neutral['500']};
        padding: ${spacing * 2}px;
        font-weight: 500;
        position: sticky;
        top: 0;
        background: ${palette.neutral['50']}${opacities['90']};
        backdrop-filter: blur(2px);
    `
)

export const StyledSelectList = styled(SelectList)`
    overflow-y: auto;
`

export const StyledSelectComboboxList = styled(ComboboxList)`
    overflow-y: auto;
`

export const makeSelectItemBaseStyle = ({palette, spacing, typography, transition}: DefaultTheme) => css`
    ${typography.textSm};
    display: flex;
    gap: ${spacing * 2}px;
    padding: ${spacing * 2}px ${spacing * 2}px;
    align-items: center;
    cursor: pointer;
    border-radius: 6px;
    margin: 1px 0;
    color: ${palette.neutral[950]};
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: ${transition};

    &[aria-disabled='true'] {
        color: ${palette.neutral['400']};
    }

    &[aria-selected='true'] {
        font-weight: 500;
    }

    &:not([aria-selected='true'])[data-active-item='true'] {
        background-color: ${palette.neutral['100']};
    }
`

export const StyledSelectItem = styled(SelectItem)(
    ({theme}) => css`
        ${makeSelectItemBaseStyle(theme)}
    `
)

export const StyledSelectItemContent = styled.div`
    white-space: normal;
`

export const StyledSelectTrigger = styled(Button)<{$hasChildren: boolean; $hasError: boolean}>(
    ({theme: {palette, shadows, spacing}, $hasChildren, $hasError}) => css`
        justify-content: space-between;
        box-shadow: ${$hasError ? 'none' : shadows.sm};
        outline: 1px solid ${$hasError ? palette.danger['300'] : palette.neutral['300']};
        font-weight: 400;
        padding: initial ${spacing * 2}px;

        ${!$hasChildren &&
        css`
            color: ${palette.neutral['400']};
        `};

        &:not(:disabled):hover,
        &:focus,
        &[aria-expanded='true'] {
            outline: 1px solid ${$hasError ? palette.danger['300'] : palette.primary['300']};
            box-shadow: ${`${shadows.xs}, 0px 0px 0px 4px ${
                $hasError ? palette.danger['100'] : palette.primary['100']
            }`};
        }
        &:disabled {
            background-color: ${palette.neutral['100']};
        }
    `
)

export const StyledSelectArrow = styled(SelectArrow)<{$isOpen: boolean}>(
    ({theme: {transition, palette}, $isOpen}) => css`
        fill: ${palette.neutral[500]};
        transition: ${transition};
        margin-left: auto;
        ${$isOpen &&
        css`
            transform: rotate(180deg);
        `}
    `
)

export const StyledSelectVirtualList: typeof Virtuoso = styled(Virtuoso)(
    ({theme: {palette}}) => css`
        height: 200px !important;
        &::-webkit-scrollbar {
            width: ${DESKTOP_TABLE_SCROLLBAR_WIDTH};
            background-color: ${palette.neutral[50]};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${palette.neutral[300]};
            border-radius: 3px;

            &:hover {
                background-color: ${palette.neutral[400]};
            }
        }
    `
)
